import {
  CloudArrowUpIcon,
  EyeIcon,
  PaintBrushIcon,
  PencilIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import clsx from 'clsx';
import apiService from '../services/api';
import jsPDF from 'jspdf';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';

interface HandbookSection {
  id: number;
  gym_id: number;
  section_key: string;
  title: string;
  content?: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface HandbookSettings {
  id: number | null;
  gym_id: number;
  primary_color: string;
  secondary_color: string;
  logo_url?: string | null;
  header_content?: string | null;
  footer_content?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const DEFAULT_SECTIONS = [
  { key: 'company_overview', title: 'Company Overview' },
  { key: 'mission_vision', title: 'Mission & Vision' },
  { key: 'core_values', title: 'Core Values' },
  { key: 'pricing', title: 'Pricing & Membership' },
  { key: 'policies', title: 'Policies & Procedures' },
  { key: 'employee_guidelines', title: 'Employee Guidelines' },
  { key: 'member_guidelines', title: 'Member Guidelines' },
  { key: 'contact', title: 'Contact Information' }
];

// Custom FontSize extension
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => {
              const fontSize = element.style.fontSize;
              if (!fontSize) return null;
              return fontSize.replace('px', '');
            },
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}px`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    } as any;
  },
});

const HandbookPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { gym } = useAuth();
  const { theme } = useTheme();
  usePageTitle('Handbook');

  const [sections, setSections] = useState<HandbookSection[]>([]);
  const [settings, setSettings] = useState<HandbookSettings | null>(null);
  const [activeSection, setActiveSection] = useState<string>('company_overview');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview' | 'customize'>('edit');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form states
  const [sectionForms, setSectionForms] = useState<Record<string, { title: string; content: string }>>({});
  const [settingsForm, setSettingsForm] = useState({
    primary_color: '#f97316',
    secondary_color: '#3b82f6',
    logo_url: ''
  });

  const previewRef = useRef<HTMLDivElement>(null);

  const loadHandbook = useCallback(async () => {
    try {
      setIsLoading(true);
      const [sectionsData, settingsData] = await Promise.all([
        apiService.getHandbookSections(),
        apiService.getHandbookSettings()
      ]);

      setSections(sectionsData);
      setSettings(settingsData);

      // Initialize section forms
      const forms: Record<string, { title: string; content: string }> = {};
      DEFAULT_SECTIONS.forEach(section => {
        const existing = sectionsData.find(s => s.section_key === section.key);
        forms[section.key] = {
          title: existing?.title || section.title,
          content: existing?.content || ''
        };
      });
      setSectionForms(forms);

      // Initialize settings form
      setSettingsForm({
        primary_color: settingsData.primary_color || '#f97316',
        secondary_color: settingsData.secondary_color || '#3b82f6',
        logo_url: settingsData.logo_url || ''
      });
    } catch (error) {
      console.error('Failed to load handbook:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load handbook'
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHandbook();
  }, [loadHandbook]);

  // Scroll to active section when preview tab is active
  useEffect(() => {
    if (activeTab === 'preview' && previewRef.current) {
      const activeSectionElement = document.getElementById(`section-${activeSection}`);
      if (activeSectionElement) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          activeSectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [activeSection, activeTab]);

  const handleSectionChange = (key: string, field: 'title' | 'content', value: string) => {
    setSectionForms(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const saveSection = async (key: string) => {
    const form = sectionForms[key];
    if (!form || !form.title.trim()) {
      setMessage({
        type: 'error',
        text: 'Please enter a section title'
      });
      return;
    }

    try {
      setIsSaving(true);
      setMessage(null);
      await apiService.updateHandbookSection(key, {
        title: form.title,
        content: form.content || '',
        order_index: DEFAULT_SECTIONS.findIndex(s => s.key === key)
      });
      setMessage({
        type: 'success',
        text: 'Section saved successfully!'
      });
      await loadHandbook();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save section'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({
        type: 'error',
        text: 'Please select a valid image file'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({
        type: 'error',
        text: 'File size must be less than 5MB'
      });
      return;
    }

    setIsUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiService.uploadGymLogo(formData);
      
      setSettingsForm(prev => ({
        ...prev,
        logo_url: response.logo_url
      }));

      setMessage({
        type: 'success',
        text: 'Logo uploaded successfully!'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload logo'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const useGymLogo = async () => {
    try {
      const gymInfo = await apiService.getGymInfo();
      if (gymInfo.logo_url) {
        setSettingsForm(prev => ({
          ...prev,
          logo_url: gymInfo.logo_url || ''
        }));
        setMessage({
          type: 'success',
          text: 'Gym logo loaded successfully!'
        });
      } else {
        setMessage({
          type: 'error',
          text: 'No logo found in gym settings'
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: 'Failed to load gym logo'
      });
    }
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);
      await apiService.updateHandbookSettings({
        primary_color: settingsForm.primary_color,
        secondary_color: settingsForm.secondary_color,
        logo_url: settingsForm.logo_url || null
      });
      setMessage({
        type: 'success',
        text: 'Settings saved successfully!'
      });
      await loadHandbook();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save settings'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const exportToPDF = () => {
    if (!previewRef.current) return;

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const element = previewRef.current;
      
      // Get text content (simplified - for better results, consider using html2canvas)
      const text = element.innerText || element.textContent || '';
      const lines = pdf.splitTextToSize(text, 180);
      
      let y = 20;
      lines.forEach((line: string) => {
        if (y > 280) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, 10, y);
        y += 7;
      });
      
      pdf.save('handbook.pdf');
      setMessage({
        type: 'success',
        text: 'PDF exported successfully!'
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setMessage({
        type: 'error',
        text: 'Failed to export PDF'
      });
    }
  };

  const currentForm = sectionForms[activeSection] || { title: '', content: '' };

  // Tiptap editor instance
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextStyle,
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: currentForm.content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      handleSectionChange(activeSection, 'content', html);
    },
    editorProps: {
      attributes: {
        class: clsx(
          "focus:outline-none min-h-[300px] p-4",
          theme === 'dark' ? "text-gray-200" : "text-gray-900"
        ),
      },
    },
  });

  // Update editor content when active section changes or form data loads
  useEffect(() => {
    if (editor && currentForm) {
      const currentHtml = editor.getHTML();
      const newContent = currentForm.content || '';
      // Only update if content is actually different and not just empty paragraph
      const normalizedCurrent = currentHtml.trim();
      const normalizedNew = newContent.trim();
      if (normalizedCurrent !== normalizedNew) {
        // Use a flag to prevent update loops
        editor.commands.setContent(newContent || '<p></p>', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, currentForm.content]);

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className={clsx(
          "text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Loading handbook...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={clsx(
              "text-4xl font-bold bg-clip-text text-transparent mb-2",
              theme === 'dark'
                ? "bg-gradient-to-r from-white to-gray-300"
                : "bg-gradient-to-r from-gray-900 to-gray-700"
            )}>
              Handbook
            </h1>
            <p className={clsx(
              "text-lg transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-600"
            )}>
              Create and manage your gym's handbook
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToPDF}
              className={clsx(
                "flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200",
                theme === 'dark'
                  ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
              )}
            >
              <PrinterIcon className="h-5 w-5" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={clsx(
            "mb-4 p-4 rounded-xl transition-colors",
            message.type === 'success'
              ? theme === 'dark' ? "bg-green-500/20 border border-green-500/50 text-green-300" : "bg-green-50 border border-green-200 text-green-800"
              : theme === 'dark' ? "bg-red-500/20 border border-red-500/50 text-red-300" : "bg-red-50 border border-red-200 text-red-800"
          )}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className={clsx(
          "flex gap-2 border-b transition-colors",
          theme === 'dark' ? "border-gray-700" : "border-gray-300"
        )}>
          {[
            { id: 'edit', label: 'Edit', icon: PencilIcon },
            { id: 'preview', label: 'Preview', icon: EyeIcon },
            { id: 'customize', label: 'Customize', icon: PaintBrushIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'edit' | 'preview' | 'customize')}
              className={clsx(
                "flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2",
                activeTab === tab.id
                  ? theme === 'dark'
                    ? "border-orange-400 text-orange-300"
                    : "border-orange-500 text-orange-600"
                  : clsx(
                      "border-transparent",
                      theme === 'dark'
                        ? "text-gray-400 hover:text-gray-300 hover:border-gray-300"
                        : "text-gray-600 hover:text-gray-900 hover:border-gray-400"
                    )
              )}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Sections */}
        <div className={clsx(
          "lg:col-span-1 backdrop-blur-lg rounded-2xl border p-4 transition-colors",
          theme === 'dark'
            ? "bg-slate-800/60 border-slate-700/50"
            : "bg-white/80 border-gray-200/50"
        )}>
          <h2 className={clsx(
            "text-lg font-semibold mb-4 transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>
            Sections
          </h2>
          <div className="space-y-2">
            {DEFAULT_SECTIONS.map(section => {
              const sectionData = sections.find(s => s.section_key === section.key);
              const formTitle = sectionForms[section.key]?.title;
              const displayTitle = formTitle || sectionData?.title || section.title;
              const isActive = activeSection === section.key;
              return (
                <button
                  key={section.key}
                  onClick={() => setActiveSection(section.key)}
                  className={clsx(
                    "w-full text-left p-3 rounded-lg transition-all duration-200",
                    isActive
                      ? theme === 'dark'
                        ? "bg-gradient-to-r from-orange-500/20 to-blue-500/20 border border-orange-400/30"
                        : "bg-gradient-to-r from-orange-50 to-blue-50 border border-orange-300"
                      : theme === 'dark'
                        ? "hover:bg-white/5 border border-transparent"
                        : "hover:bg-gray-50 border border-transparent"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className={clsx(
                      "font-medium truncate transition-colors",
                      isActive
                        ? theme === 'dark' ? "text-orange-300" : "text-orange-600"
                        : theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>
                      {displayTitle}
                    </div>
                    {sectionData?.content && (
                      <div className={clsx(
                        "text-xs mt-1 truncate transition-colors",
                        theme === 'dark' ? "text-gray-500" : "text-gray-500"
                      )}>
                        {sectionData.content.substring(0, 30)}...
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className={clsx(
          "lg:col-span-3 backdrop-blur-lg rounded-2xl border p-6 transition-colors",
          theme === 'dark'
            ? "bg-slate-800/60 border-slate-700/50"
            : "bg-white/80 border-gray-200/50"
        )}>
          {activeTab === 'edit' && (
            <div>
              <div className="mb-6">
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Section Title *
                </label>
                <input
                  type="text"
                  value={currentForm.title}
                  onChange={(e) => handleSectionChange(activeSection, 'title', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 rounded-xl border transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-500 focus:border-orange-400/50 focus:ring-2 focus:ring-orange-400/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  )}
                  placeholder="Enter section title"
                />
              </div>

              <div className="mb-6">
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Content
                </label>
                <div className={clsx(
                  "rounded-xl overflow-hidden border transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20"
                    : "bg-white border-gray-300"
                )}>
                  {/* Toolbar */}
                  {editor && (
                    <div className={clsx(
                      "flex flex-wrap gap-1 p-2 border-b transition-colors",
                      theme === 'dark'
                        ? "bg-white/5 border-white/20"
                        : "bg-gray-50 border-gray-200"
                    )}>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                          editor.isActive('heading', { level: 1 })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        H1
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                          editor.isActive('heading', { level: 2 })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        H2
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                          editor.isActive('heading', { level: 3 })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        H3
                      </button>
                      <div className={clsx(
                        "w-px mx-1",
                        theme === 'dark' ? "bg-white/20" : "bg-gray-300"
                      )} />
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm font-bold transition-colors",
                          editor.isActive('bold')
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm italic transition-colors",
                          editor.isActive('italic')
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        I
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm line-through transition-colors",
                          editor.isActive('strike')
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        S
                      </button>
                      <div className={clsx(
                        "w-px mx-1",
                        theme === 'dark' ? "bg-white/20" : "bg-gray-300"
                      )} />
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors",
                          editor.isActive('bulletList')
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        •
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors",
                          editor.isActive('orderedList')
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        1.
                      </button>
                      <div className={clsx(
                        "w-px mx-1",
                        theme === 'dark' ? "bg-white/20" : "bg-gray-300"
                      )} />
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors",
                          editor.isActive({ textAlign: 'left' })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        ⬅
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors",
                          editor.isActive({ textAlign: 'center' })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        ⬌
                      </button>
                      <button
                        type="button"
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors",
                          editor.isActive({ textAlign: 'right' })
                            ? theme === 'dark'
                              ? "bg-orange-500/30 text-orange-300"
                              : "bg-orange-100 text-orange-700"
                            : theme === 'dark'
                              ? "text-gray-300 hover:bg-white/10"
                              : "text-gray-700 hover:bg-gray-100"
                        )}
                      >
                        ➡
                      </button>
                      <div className={clsx(
                        "w-px mx-1",
                        theme === 'dark' ? "bg-white/20" : "bg-gray-300"
                      )} />
                      <select
                        onChange={(e) => {
                          const fontSize = e.target.value;
                          if (fontSize === '') {
                            // Remove font size by unsetting the textStyle mark
                            editor.chain().focus().unsetMark('textStyle').run();
                          } else {
                            // Use type assertion to access custom command
                            (editor.chain().focus() as any).setFontSize(fontSize).run();
                          }
                        }}
                        value={(() => {
                          const attrs = editor.getAttributes('textStyle');
                          return attrs?.fontSize || '';
                        })()}
                        className={clsx(
                          "px-3 py-1.5 rounded text-sm transition-colors border cursor-pointer",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-gray-300 hover:bg-white/20"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        )}
                      >
                        <option value="">Font Size</option>
                        <option value="10">10px</option>
                        <option value="12">12px</option>
                        <option value="14">14px</option>
                        <option value="16">16px</option>
                        <option value="18">18px</option>
                        <option value="20">20px</option>
                        <option value="24">24px</option>
                        <option value="28">28px</option>
                        <option value="32">32px</option>
                        <option value="36">36px</option>
                        <option value="48">48px</option>
                      </select>
                    </div>
                  )}
                  {/* Editor Content */}
                  <div className={clsx(
                    "min-h-[300px]",
                    theme === 'dark' ? "text-gray-200" : "text-gray-900"
                  )}>
                    <EditorContent editor={editor} />
                  </div>
                </div>
              </div>

              <button
                onClick={() => saveSection(activeSection)}
                disabled={isSaving}
                className={clsx(
                  "px-6 py-3 rounded-xl font-medium transition-all duration-200",
                  theme === 'dark'
                    ? "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                    : "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                )}
              >
                {isSaving ? 'Saving...' : 'Save Section'}
              </button>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="relative max-h-[800px] overflow-y-auto">
              <div
                ref={previewRef}
                className={clsx(
                  "prose prose-lg max-w-none transition-colors",
                  theme === 'dark' ? "prose-invert" : ""
                )}
                style={{
                  '--tw-prose-body': theme === 'dark' ? '#e5e7eb' : '#374151',
                  '--tw-prose-headings': theme === 'dark' ? '#ffffff' : '#111827',
                  '--tw-prose-links': settings?.primary_color || '#f97316',
                  '--tw-prose-bold': theme === 'dark' ? '#ffffff' : '#111827',
                  '--tw-prose-code': theme === 'dark' ? '#e5e7eb' : '#374151',
                  '--tw-prose-pre-bg': theme === 'dark' ? '#1f2937' : '#f3f4f6'
                } as React.CSSProperties}
              >
                {settings?.logo_url && (
                  <div className="mb-6 text-center">
                    <img
                      src={settings.logo_url}
                      alt="Gym Logo"
                      className="h-20 mx-auto"
                    />
                  </div>
                )}
                {DEFAULT_SECTIONS.map((defaultSection) => {
                  // Find saved section data if it exists
                  const savedSection = sections.find(s => s.section_key === defaultSection.key);
                  // Get form data (which includes unsaved changes)
                  const formData = sectionForms[defaultSection.key];
                  
                  // Use form title if available, otherwise saved title, otherwise default title
                  const displayTitle = formData?.title || savedSection?.title || defaultSection.title;
                  // Use saved content if available
                  const displayContent = savedSection?.content || '';
                  
                  return (
                    <div 
                      key={defaultSection.key} 
                      id={`section-${defaultSection.key}`}
                      className="mb-8 scroll-mt-4"
                    >
                      <h2
                        style={{ color: settings?.primary_color || '#f97316' }}
                        className="text-3xl font-bold mb-4"
                      >
                        {displayTitle}
                      </h2>
                      {displayContent && (
                        <div
                          dangerouslySetInnerHTML={{ __html: displayContent }}
                          className={clsx(
                            "transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-700"
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'customize' && (
            <div>
              <h2 className={clsx(
                "text-2xl font-bold mb-6 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>
                Visual Customization
              </h2>

              <div className="space-y-6">
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Primary Color
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={settingsForm.primary_color}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="h-12 w-24 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settingsForm.primary_color}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, primary_color: e.target.value }))}
                      className={clsx(
                        "flex-1 px-4 py-3 rounded-xl border transition-colors font-mono",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={settingsForm.secondary_color}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="h-12 w-24 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settingsForm.secondary_color}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className={clsx(
                        "flex-1 px-4 py-3 rounded-xl border transition-colors font-mono",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Logo
                  </label>
                  <div className="space-y-3">
                    {/* Option to use gym logo */}
                    <button
                      type="button"
                      onClick={useGymLogo}
                      className={clsx(
                        "w-full px-4 py-2 rounded-lg border transition-colors text-sm font-medium",
                        theme === 'dark'
                          ? "bg-white/5 border-white/20 text-white hover:bg-white/10"
                          : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      Use Logo from Gym Settings
                    </button>
                    
                    {/* Upload new logo */}
                    <div>
                      <label className={clsx(
                        "block text-sm font-medium mb-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Or Upload New Logo
                      </label>
                      <div className="flex items-center gap-3">
                        <label className={clsx(
                          "flex-1 px-4 py-3 rounded-xl border cursor-pointer transition-colors flex items-center justify-center gap-2",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        )}>
                          <CloudArrowUpIcon className="h-5 w-5" />
                          {isUploading ? 'Uploading...' : 'Choose File'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={isUploading}
                          />
                        </label>
                      </div>
                      {settingsForm.logo_url && (
                        <div className="mt-3">
                          <img
                            src={settingsForm.logo_url}
                            alt="Current logo"
                            className="h-20 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={saveSettings}
                  disabled={isSaving}
                  className={clsx(
                    "px-6 py-3 rounded-xl font-medium transition-all duration-200",
                    theme === 'dark'
                      ? "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                      : "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                  )}
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HandbookPage;

